import { $$ } from "@tento-ui/fastdom";
import { FRAME_BUDGET, type FrameBudget, UnitCost } from "@tento-ui/frame-budget";
import { FrameMarks } from "@tento-ui/frame-marks";
import { Tracing } from "@tento-core/tracing";
import type { RowLocalId } from "@tento-lona/sheets";

/**
 * Per-row fixed-children applies, drained under the shared per-frame budget
 * (`@tento-ui/frame-budget`) when `scroll-frame-deadline` is on.
 *
 * A visible-columns emit or a spec completion queues one unit per row and
 * owner (a later unit for the same key replaces the queued one). Each frame
 * the queue runs units nearest the row viewport first (rank 0 = visible),
 * charges what each spent, and stops when the frame has nothing left, always
 * running at least one unit so nothing starves. What did not fit runs in the
 * next frame; nothing waits for settle. While units are queued the queue is
 * background debt, so the scroll engine does not report "bound" early.
 *
 * How these rules were measured and chosen is in the lona-dom project docs
 * (perf-policy-history.md).
 */
export class FixedChildrenQueue {
  readonly #units = new Map<string, FixedChildrenQueue.Unit>();
  #scheduled = false;
  /** What this queue's units usually cost, for the frame's admission check. */
  readonly #cost = new UnitCost();

  constructor(
    readonly budget:
      & Pick<FrameBudget, "mayRunUnit" | "charge" | "addDebt" | "clearDebt">
      & Partial<Pick<FrameBudget, "notePace">>,
    readonly scheduler: FixedChildrenQueue.Scheduler,
    /** Per drain: a unit's distance from the row viewport (0 = visible); smaller runs first. */
    readonly ranker: () => (unit: FixedChildrenQueue.Unit) => number,
  ) {}

  /** A queue on the document's frame budget and fastdom. */
  static forDocument(ranker: () => (unit: FixedChildrenQueue.Unit) => number): FixedChildrenQueue {
    return new FixedChildrenQueue(FRAME_BUDGET, {
      thisFrame: (task) => $$.mutate(task),
      nextFrame: (task) => $$.measureNext(() => $$.mutate(task)),
      now: () => performance.now(),
    }, ranker);
  }

  /** How many units are queued. */
  get size(): number {
    return this.#units.size;
  }

  /** Queue `unit`, replacing a queued unit with the same key. */
  enqueue(unit: FixedChildrenQueue.Unit): void {
    this.#units.delete(unit.key);
    this.#units.set(unit.key, unit);
    this.budget.addDebt(this);
    this.#schedule(false);
  }

  /** Drop every queued unit (a rebind or detach supersedes them). */
  clear(): void {
    this.#units.clear();
    this.budget.clearDebt(this);
  }

  #schedule(next: boolean): void {
    if (this.#scheduled) return;
    this.#scheduled = true;
    const task = () => this.#drain();
    if (next) this.scheduler.nextFrame(task);
    else this.scheduler.thisFrame(task);
  }

  #drain(): void {
    this.#scheduled = false;
    const order = this.#ranked();
    const ran = this.#run(order);
    this.budget.notePace?.("fixed", order.length, ran, this.#units.size);
    this.#settle();
  }

  /** The units queued now, nearest the row viewport first. */
  #ranked(): { unit: FixedChildrenQueue.Unit; rank: number }[] {
    const rank = this.ranker();
    return [...this.#units.values()]
      .map((unit) => ({ unit, rank: rank(unit) }))
      .sort((a, b) => a.rank - b.rank);
  }

  /** Run `order`'s units while the frame admits them; returns how many ran. */
  #run(order: readonly { unit: FixedChildrenQueue.Unit; rank: number }[]): number {
    let ran = 0;
    for (const { unit, rank } of order) {
      if (!this.budget.mayRunUnit(ran, this.scheduler.now(), this.#cost)) break;
      this.#units.delete(unit.key);
      const start = this.scheduler.now();
      const mark = FrameMarks.begin();
      try {
        unit.apply();
      } catch (error) {
        Tracing.err("ui")("fixed children unit failed", { key: unit.key, error: String(error) });
      } finally {
        FrameMarks.end("fixed.unit", mark, rank, this.#units.size);
        const elapsed = this.scheduler.now() - start;
        this.#cost.note(elapsed);
        this.budget.charge(elapsed);
      }
      ran++;
    }
    return ran;
  }

  /** Queue the next frame's drain while units remain, else clear the debt. */
  #settle(): void {
    if (this.#units.size > 0) this.#schedule(true);
    else this.budget.clearDebt(this);
  }
}

export namespace FixedChildrenQueue {
  /** How far the span [top, bottom) lies outside the viewport [viewTop, viewBottom) in px; 0 = it intersects. */
  export function viewportDistancePx(top: number, bottom: number, viewTop: number, viewBottom: number): number {
    return Math.max(0, viewTop - bottom, top - viewBottom);
  }
  /** One row's (and owner's) fixed-children apply. */
  export type Unit = {
    /** `<row>::<owner>`; a later unit with the same key replaces a queued one. */
    readonly key: string;
    /** The row whose fixed children it applies (for ranking). */
    readonly rowId: RowLocalId;
    readonly apply: () => void;
  };
  /** How the queue reaches the frame loop (fastdom in production, a fake in tests). */
  export type Scheduler = {
    /** Run `task` in this frame's write phase (or the next one outside a flush). */
    readonly thisFrame: (task: () => void) => void;
    /** Run `task` in the next frame's write phase. */
    readonly nextFrame: (task: () => void) => void;
    readonly now: () => number;
  };
}
